// Plane: simulation entity + visual root.
// Simplified arcade flight model — no real aerodynamics, just feels like flying.

import * as THREE from 'three';
import { createPlaneMesh, SHAPE_ASPECTS, type PaperColor, type PlaneShape } from './PlaneMesh';
import type { InputState } from '../core/Input';

export type Team = 'blue' | 'red';

export interface PlaneInit {
  shape: PlaneShape;
  color: PaperColor;
  team: Team;
  position: THREE.Vector3;
  forward?: THREE.Vector3;
  isPlayer?: boolean;
  callsign?: string;
}

const ARENA_RADIUS = 280;
const ARENA_FLOOR = -40;
const ARENA_CEIL = 160;

export class Plane {
  group: THREE.Group;
  position: THREE.Vector3;
  velocity = new THREE.Vector3();
  forward: THREE.Vector3;
  up = new THREE.Vector3(0, 1, 0);
  right = new THREE.Vector3(1, 0, 0);
  hp = 3;
  maxHp = 3;
  team: Team;
  shape: PlaneShape;
  color: PaperColor;
  isPlayer: boolean;
  callsign: string;
  alive = true;

  // smoothed control state to avoid jittery response
  private smoothPitch = 0;
  private smoothYaw = 0;
  private smoothRoll = 0;
  private smoothThrottle = 0.5;

  fireCooldown = 0;
  lastFiredAt = -Infinity;
  invulnUntil = 0;

  constructor(init: PlaneInit) {
    this.shape = init.shape;
    this.color = init.color;
    this.team = init.team;
    this.isPlayer = init.isPlayer ?? false;
    this.callsign = init.callsign ?? (init.isPlayer ? 'PLAYER' : 'AI');
    this.position = init.position.clone();
    this.forward = (init.forward ?? new THREE.Vector3(0, 0, 1)).clone().normalize();
    this.right.crossVectors(this.forward, this.up).normalize();

    this.group = createPlaneMesh({ shape: init.shape, color: init.color });
    this.group.position.copy(this.position);
    this.group.lookAt(this.position.clone().add(this.forward));

    // Tint nose stripe by team for quick at-a-glance team identification.
    const stripeColor = init.team === 'blue' ? 0x66ccff : 0xff6655;
    const stripeGeom = new THREE.BufferGeometry();
    const noseZ = init.shape === 'dart' ? 1.4 : init.shape === 'glider' ? 0.9 : 1.0;
    stripeGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -0.05, 0.002, noseZ - 0.05,    0.05, 0.002, noseZ - 0.05,    0, 0.06, noseZ - 0.05,
    ]), 3));
    stripeGeom.computeVertexNormals();
    const stripeMat = new THREE.MeshBasicMaterial({ color: stripeColor });
    this.group.add(new THREE.Mesh(stripeGeom, stripeMat));
  }

  get aspects() { return SHAPE_ASPECTS[this.shape]; }

  applyInput(input: InputState, dt: number): void {
    if (!this.alive) return;

    // Smooth controls for arcade feel.
    const k = 1 - Math.exp(-dt * 8);
    this.smoothPitch += (input.pitch - this.smoothPitch) * k;
    this.smoothYaw += (input.yaw - this.smoothYaw) * k;
    this.smoothRoll += (input.roll - this.smoothRoll) * k;
    const targetThrottle = input.boost ? 1.0 : input.brake ? 0.25 : input.throttle;
    this.smoothThrottle += (targetThrottle - this.smoothThrottle) * k * 0.5;

    const turn = this.aspects.turnRate;

    // Apply yaw and pitch by rotating the forward vector around the right and up axes.
    const yawAxis = this.up;
    const pitchAxis = this.right;

    const yawAng = -this.smoothYaw * turn * dt;
    const pitchAng = this.smoothPitch * turn * 0.8 * dt;
    const rollAng = -this.smoothRoll * turn * 1.4 * dt;

    this.forward.applyAxisAngle(yawAxis, yawAng).normalize();
    this.forward.applyAxisAngle(pitchAxis, pitchAng).normalize();

    // Recompute right vector from forward, then roll up vector around forward.
    this.right.crossVectors(this.forward, new THREE.Vector3(0, 1, 0)).normalize();
    this.up.crossVectors(this.right, this.forward).normalize();
    this.up.applyAxisAngle(this.forward, rollAng).normalize();
    this.right.crossVectors(this.forward, this.up).normalize();

    // Compute target speed from throttle & top speed; lerp velocity.
    const targetSpeed = this.aspects.topSpeed * this.smoothThrottle;
    const desiredVel = this.forward.clone().multiplyScalar(targetSpeed);
    this.velocity.lerp(desiredVel, 1 - Math.exp(-dt * 2.5));

    // Soft world boundary: pull plane toward arena center if too far out.
    const radial = new THREE.Vector3(this.position.x, 0, this.position.z);
    const r = radial.length();
    if (r > ARENA_RADIUS) {
      radial.multiplyScalar(-1 / r);
      this.velocity.addScaledVector(radial, (r - ARENA_RADIUS) * 0.6 * dt);
    }
    if (this.position.y < ARENA_FLOOR) this.velocity.y += (ARENA_FLOOR - this.position.y) * 2.0 * dt;
    if (this.position.y > ARENA_CEIL) this.velocity.y -= (this.position.y - ARENA_CEIL) * 1.5 * dt;

    this.position.addScaledVector(this.velocity, dt);

    // Banking visual flair: extra roll based on yaw input even outside player intent.
    const bankAng = this.smoothYaw * 0.5;
    const bankUp = this.up.clone().applyAxisAngle(this.forward, bankAng);

    // Update group transform.
    this.group.position.copy(this.position);
    const m = new THREE.Matrix4().lookAt(
      new THREE.Vector3(0, 0, 0),
      this.forward.clone().multiplyScalar(-1),
      bankUp,
    );
    this.group.quaternion.setFromRotationMatrix(m);

    if (this.fireCooldown > 0) this.fireCooldown -= dt;
  }

  /** Try to fire; returns true if a shot was emitted. Caller spawns the bullet. */
  tryFire(now: number): boolean {
    if (!this.alive) return false;
    if (this.fireCooldown > 0) return false;
    this.fireCooldown = 0.18;
    this.lastFiredAt = now;
    return true;
  }

  takeDamage(amount: number, now: number): boolean {
    if (!this.alive) return false;
    if (now < this.invulnUntil) return false;
    this.hp -= amount;
    this.invulnUntil = now + 0.25;
    if (this.hp <= 0) {
      this.alive = false;
      this.hp = 0;
      return true; // killed
    }
    return false;
  }
}

export const ARENA = { RADIUS: ARENA_RADIUS, FLOOR: ARENA_FLOOR, CEIL: ARENA_CEIL };
